// Redux Middleware

import { normalize } from "normalizr";
import fetch from "lib/fetch";
import querystring from "querystring";
import me from "models/me";
import { APIError } from "lib/errors";

import { UNAUTHORIZED } from "constants/status-codes";
import { CALL_API } from "constants/api";

const recordCache = {};

const getModelForSchema = (store, schemaId: string, builder) => {
    let Record = recordCache[schemaId];
    if (!Record) {
        // build Record
        const schemas = store.getState().entities.schemas;
        const schema = schemas[schemaId];
        if (!schema) {
            // this should never happen
            throw new Error("Schema not found");
        }

        Record = builder.create ? builder.create(schema) : builder(schema);
        recordCache[schemaId] = Record;
    }
    return Record;
};

// Fetches an API response and normalizes the result JSON according to schema.
// This makes every API response have the same shape, regardless of how nested it was.
const fetchData = (
    endpoint: string,
    { body, currentPage = 1, fetchAll = false, maxPages, method = "GET", prevData, records = {}, schema }: any,
    store,
) => {
    if (body) {
        body = JSON.stringify(body);
    }
    return fetch(endpoint, {
        body,
        method,
    }).then((response) =>
        response.json().then((json) => {
            if (!response.ok) {
                return Promise.reject(
                    new APIError(json.message || "Something bad happened", response.status, response.statusText),
                );
            }

            json = prevData ? prevData.concat(json) : json;
            const nextPage = response.headers.get("next");
            if (fetchAll && nextPage && (!maxPages || currentPage < maxPages)) {
                return fetchData(
                    nextPage,
                    {
                        currentPage: currentPage++,
                        fetchAll: true,
                        method: "GET",
                        prevData: json,
                    },
                    store,
                );
            }

            if (!schema) {
                return { data: json, response };
            }

            const data = normalize(json, schema);

            Object.keys(records).forEach((key) => {
                const recordOrBuilder = records[key];
                let Record = recordOrBuilder;
                const collection = data.entities[key];
                if (collection) {
                    const keys = Object.keys(collection);
                    if (keys.length) {
                        // FDM
                        if (typeof recordOrBuilder === "object") {
                            const schemaId = collection[keys[0]].__schema_id;
                            Record = getModelForSchema(store, schemaId, recordOrBuilder);
                        }
                        keys.forEach((id) => {
                            collection[id] = new Record(collection[id]);
                        });
                    }
                }
            });

            return { data, response };
        }),
    );
};

export interface IResponse {
    error?: APIError;
    data: any;
    [params: string]: any;
}

// A Redux middleware that interprets actions with CALL_API info specified.
// Performs the call and promises when such actions are dispatched.
export default (store) => (next) => (action) => {
    if (!action) {
        return next();
    }
    const callAPI = action[CALL_API];
    if (typeof callAPI === "undefined") {
        return next(action);
    }

    let { endpoint } = callAPI;
    const { body, fetchAll, maxPages, meta = {}, method, onError, onSuccess, query, records, schema, types } = callAPI;

    if (typeof endpoint === "function") {
        endpoint = endpoint(store.getState());
    }

    if (typeof endpoint !== "string") {
        throw new Error("Specify a string endpoint URL.");
    }
    // if (!schema) {
    //    throw new Error('Specify one of the exported Schemas.');
    // }
    if (!Array.isArray(types) || types.length !== 3) {
        throw new Error("Expected an array of three action types.");
    }

    if (query) {
        endpoint = `${endpoint}?${querystring.stringify(query)}`;
    }

    const actionWith = (data) => {
        const finalAction = { ...action, ...data };
        delete finalAction[CALL_API];
        return finalAction;
    };

    const [requestType, successType, failureType] = types;
    next(
        actionWith({
            type: requestType,
            ...meta,
        }),
    );

    return fetchData(endpoint, { body, fetchAll, maxPages, method, records, schema }, store).then(
        ({ data, response }) => {
            if (onSuccess) {
                onSuccess({ data, response, type: successType });
            }
            next(
                actionWith({
                    data,
                    response,
                    type: successType,
                    ...meta,
                }),
            );
        },
        (error: APIError) => {
            if (me.isSignedIn() && error.code === UNAUTHORIZED) {
                me.logout(); // TODO: Move to redux
            } else {
                if (onError) {
                    onError({ type: failureType, error });
                }
                next(
                    actionWith({
                        error,
                        type: failureType,
                        ...meta,
                    }),
                );
            }
        },
    );
};
